/**
 * Error references and report composition.
 *
 * A person who just hit a crash has one thing that makes their report useful:
 * a short code that points at that exact event. Without it the message is "it
 * broke", and the Sentry event (when consent allowed one to be sent) cannot be
 * found. The code is generated in the browser so it exists even when telemetry
 * is switched off, and it is attached to the Sentry event as a tag, so the
 * report and the event can be joined later.
 *
 * The composed report is intentionally plain text: it goes into the user's own
 * mail client or their clipboard, never over a network on its own, so nothing
 * leaves the page until the person decides to send it.
 */

/**
 * Crockford-style alphabet. No 0/O, 1/I/L: a code read over the phone or
 * retyped from a screenshot still resolves to the same event.
 *
 * The code carries no brand prefix on purpose. A company rename must not
 * invalidate references already sitting in someone's inbox, and the label
 * around the code already tells the reader what it is.
 */
const ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
const REFERENCE_LENGTH = 7;
const SUMMARY_LIMIT = 160;
/** Mail clients truncate long `mailto` bodies; stay well inside the limit. */
const BODY_LIMIT = 1800;

/**
 * Where the report came from, in the person's terms rather than ours.
 *
 * `report` is the standing "Report an issue" entry, which is not a failure:
 * it files a note without inventing an error that did not happen.
 */
export type ErrorSurface = 'page' | 'inline' | 'route' | 'report';

const SURFACE_LABEL: Record<ErrorSurface, string> = {
  page: 'Full-page error screen',
  inline: 'Page error inside a portal',
  route: 'Page failed to load',
  report: 'Reported from the app',
};

export interface ErrorReportContext {
  /** Quotable code, e.g. `7K4M2QX`. */
  reference: string;
  /** ISO timestamp of the failure. */
  occurredAt: string;
  /** Path only: query strings can carry invite tokens, so they are dropped. */
  route: string;
  surface: ErrorSurface;
  /** Error name and first line; never a stack or a full payload. */
  summary: string;
}

export interface ErrorReportInput {
  whatHappened: string;
  expected?: string;
}

/**
 * The only brand-bound value in this module: where reports land. Kept in one
 * place so a rename or a dedicated support mailbox is a single-line change.
 */
export const REPORT_EMAIL = 'info@akiva.com';

/**
 * Seven characters from the unambiguous alphabet. 31^7 ≈ 2.8e10, enough that a
 * collision inside one person's report history is not a concern.
 */
export function makeErrorReference(random: () => number = Math.random): string {
  let code = '';
  for (let index = 0; index < REFERENCE_LENGTH; index += 1) {
    code += ALPHABET[Math.floor(random() * ALPHABET.length)] ?? ALPHABET[0];
  }
  return code;
}

/** Error name plus the first message line, collapsed and bounded. */
export function summarizeError(error: unknown): string {
  const name = error instanceof Error ? error.name : 'Error';
  const raw = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : '';
  const first = raw.split('\n')[0].replace(/\s+/g, ' ').trim();
  if (!first) return name;
  return `${name}: ${first}`.slice(0, SUMMARY_LIMIT);
}

export function errorReportContext(
  surface: ErrorSurface,
  error: unknown,
  options: { route?: string; now?: Date; random?: () => number } = {},
): ErrorReportContext {
  const route = options.route
    ?? (typeof window === 'undefined' ? '/' : window.location.pathname);
  return {
    reference: makeErrorReference(options.random),
    occurredAt: (options.now ?? new Date()).toISOString(),
    route: route || '/',
    surface,
    summary: summarizeError(error),
  };
}

/**
 * Context for the standing "Report an issue" entry.
 *
 * No error is summarized because none happened: the person is filing a note
 * before anything breaks. It still gets a reference so the queue, the mail and
 * telemetry can join on one value.
 */
export function problemReportContext(
  options: { route?: string; now?: Date; random?: () => number } = {},
): ErrorReportContext {
  const route = options.route
    ?? (typeof window === 'undefined' ? '/' : window.location.pathname);
  return {
    reference: makeErrorReference(options.random),
    occurredAt: (options.now ?? new Date()).toISOString(),
    route: route || '/',
    surface: 'report',
    summary: '',
  };
}

/** Human-readable UTC timestamp, stable across locales and test machines. */
export function formatReportTime(iso: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso);
  return match ? `${match[1]} ${match[2]} UTC` : iso;
}

export function composeErrorReport(
  context: ErrorReportContext,
  input: ErrorReportInput,
): string {
  const lines = [
    `Reference: ${context.reference}`,
    `Page: ${context.route}`,
    `When: ${formatReportTime(context.occurredAt)}`,
    `Where: ${SURFACE_LABEL[context.surface]}`,
  ];
  if (context.summary) lines.push(`Error: ${context.summary}`);
  lines.push('', 'What happened:', input.whatHappened.trim());
  if (input.expected?.trim()) {
    lines.push('', 'What I expected:', input.expected.trim());
  }
  return lines.join('\n');
}

export function errorReportMailto(
  context: ErrorReportContext,
  input: ErrorReportInput,
  to: string = REPORT_EMAIL,
): string {
  const subject = `Issue report ${context.reference}`;
  const body = composeErrorReport(context, input).slice(0, BODY_LIMIT);
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
